import {
  Box,
  List,
  Radio,
  Text,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  BankingOnboardingFragment,
  BankingValueObjectsOnboardingCreationSourceValueEnum,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/banking/components/NewAccountModal/types'
import { WizardStepLayout } from 'modules/banking/components/NewAccountModal/wizardSteps/WizardStepLayout'
import { useBankingAccountCreationAvailability } from 'modules/banking/pages/AccountsListPage/useBankingAccountCreationAvailability'
import { getDefaultAlias } from 'modules/banking/utils'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface UseCaseOption {
  value: BankingValueObjectsOnboardingCreationSourceValueEnum
  title: string
  description: string
  disabled: boolean
  disabledLabel?: string
  reason?: 'available' | 'needs_upsell' | 'no_permission' | 'already_active'
}

interface Props {
  existingOnboardings: BankingOnboardingFragment[]
}

const MODULE_MAP: Record<string, AvailableModules> = {
  expenses: AvailableModules.expenses,
  software: AvailableModules.it_management,
  benefits: AvailableModules.benefits,
  payments: AvailableModules.finance,
  finance: AvailableModules.accounts_payable,
}

export const BankAccountTypeSelectorWizardStep = ({
  data,
  onDataChanged,
  existingOnboardings,
}: WizardStepRenderParams<WizardData> & Props) => {
  const { loading, options } = useBankingAccountCreationAvailability()

  const existingCreationSources = useUnsafeMemo(() => {
    const legalEntity = data.legalEntity
    if (!legalEntity) return new Set<string>()

    return new Set(
      existingOnboardings
        .filter((onboarding) => onboarding.legalEntity?.id === legalEntity.id)
        .map((onboarding) => onboarding.creationSource)
        .filter(Boolean)
    )
  }, [existingOnboardings, data.legalEntity])

  const allUseCaseOptions: UseCaseOption[] = useUnsafeMemo(() => {
    const hasExpenses = existingCreationSources.has('expenses')
    const hasSoftware = existingCreationSources.has('software')
    const hasPayments = existingCreationSources.has('payments')
    const hasBenefits = existingCreationSources.has('benefits')
    const hasFinance = existingCreationSources.has('finance')

    const toEnum = (
      value: 'expenses' | 'software' | 'payments' | 'benefits' | 'finance'
    ): BankingValueObjectsOnboardingCreationSourceValueEnum => {
      return value
    }

    return [
      {
        value: toEnum('expenses'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.expenses.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.expenses.description'),
        disabled: hasExpenses || !options.expenses.canCreate,
        disabledLabel: hasExpenses
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.expenses.already_active')
          : options.expenses.reason === 'needs_upsell'
          ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
          : undefined,
        reason: hasExpenses ? 'already_active' : options.expenses.reason,
      },
      {
        value: toEnum('software'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.software.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.software.description'),
        disabled: hasSoftware || !options.software.canCreate,
        disabledLabel: hasSoftware
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.software.already_active')
          : options.software.reason === 'needs_upsell'
          ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
          : undefined,
        reason: hasSoftware ? 'already_active' : options.software.reason,
      },
      {
        value: toEnum('payments'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.payments.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.payments.description'),
        disabled: hasPayments || !options.payments.canCreate,
        disabledLabel: hasPayments
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.payments.already_active')
          : options.payments.reason === 'needs_upsell'
          ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
          : undefined,
        reason: hasPayments ? 'already_active' : options.payments.reason,
      },
      {
        value: toEnum('benefits'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.benefits.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.benefits.description'),
        disabled: hasBenefits || !options.benefits.canCreate,
        disabledLabel: hasBenefits
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.benefits.already_active')
          : options.benefits.reason === 'needs_upsell'
          ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
          : undefined,
        reason: hasBenefits ? 'already_active' : options.benefits.reason,
      },
      {
        value: toEnum('finance'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.finance.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.finance.description'),
        disabled: hasFinance || !options.accountsPayable.canCreate,
        disabledLabel: hasFinance
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.finance.already_active')
          : options.accountsPayable.reason === 'needs_upsell'
          ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
          : undefined,
        reason: hasFinance ? 'already_active' : options.accountsPayable.reason,
      },
    ]
  }, [existingCreationSources, options])

  const availableOptions = useUnsafeMemo(
    () => allUseCaseOptions.filter((option) => option.reason === 'available'),
    [allUseCaseOptions]
  )

  const disabledOptionsWithUpsell = useUnsafeMemo(
    () => allUseCaseOptions.filter((option) => option.reason === 'needs_upsell'),
    [allUseCaseOptions]
  )

  const existingAccounts = useUnsafeMemo(() => {
    const legalEntity = data.legalEntity
    if (!legalEntity) return []

    return existingOnboardings.filter((onboarding) => onboarding.legalEntity?.id === legalEntity.id)
  }, [existingOnboardings, data.legalEntity])

  const handleSelectUseCase = useUnsafeCallback(
    (value: string) => {
      const validValues: ReadonlyArray<string> = [
        'expenses',
        'software',
        'payments',
        'benefits',
        'finance',
        'accounts_payable',
      ]
      const isValidSource = (
        v: string
      ): v is BankingValueObjectsOnboardingCreationSourceValueEnum => {
        return validValues.includes(v)
      }

      if (isValidSource(value)) {
        onDataChanged({
          ...data,
          creationSource: value,
        })
      }
    },
    [data, onDataChanged]
  )

  if (loading) {
    return (
      <WizardStepLayout
        title={i18n.t('banking.accounts.new_wizard.step_1_1.title')}
        subtitle={i18n.t('banking.accounts.new_wizard.step_1_1.subtitle')}
      >
        <Box gap="s24" style={{ maxWidth: '600px' }}>
          <Text.Normal color="grey700">{i18n.t('common.loading')}</Text.Normal>
        </Box>
      </WizardStepLayout>
    )
  }

  return (
    <WizardStepLayout
      title={i18n.t('banking.accounts.new_wizard.step_1_1.title')}
      subtitle={i18n.t('banking.accounts.new_wizard.step_1_1.subtitle')}
    >
      <Box gap="s24" style={{ maxWidth: '600px' }}>
        {availableOptions.length > 0 ? (
          <Box gap="s16">
            {availableOptions.map((option) => (
              <Radio
                key={option.value}
                name="account-purpose"
                description={option.description}
                option={{
                  label: option.title,
                  value: option.value,
                  disabled: false,
                }}
                value={data.creationSource || ''}
                onChange={handleSelectUseCase}
              />
            ))}
          </Box>
        ) : (
          <Box padding="s16" background="grey100" borderRadius={{ all: 'abs008' }}>
            <Text.Normal color="grey700">
              {i18n.t('banking.accounts.new_wizard.step_1_1.no_available_options')}
            </Text.Normal>
          </Box>
        )}

        {disabledOptionsWithUpsell.length > 0 && (
          <Box gap="s16">
            <Text.Normal weight="semibold" color="grey800">
              {i18n.t('banking.accounts.new_wizard.step_1_1.upgrade_required')}
            </Text.Normal>
            {disabledOptionsWithUpsell
              .map((option) => {
                const module = MODULE_MAP[option.value]
                if (!module) return null
                return (
                  <UpsellingKit.ProductCard
                    key={option.value}
                    moduleRequested={module}
                    requestSource="Banking account creation wizard"
                    requestReason={ValidReasons.MissingAddon}
                  />
                )
              })
              .filter(Boolean)}
          </Box>
        )}

        {existingAccounts.length > 0 && (
          <Box gap="s8">
            <Text.Normal weight="semibold" color="grey800">
              {i18n.t('banking.accounts.new_wizard.step_1_1.existing_accounts')}
            </Text.Normal>
            <List>
              {existingAccounts.map((onboarding) => (
                <List.Item key={onboarding.id}>
                  <Box gap="s4" flexDirection="column">
                    <Text.Normal weight="semibold">
                      {getDefaultAlias(onboarding.creationSource)}
                    </Text.Normal>
                    <Text.Normal size="100" color="grey700">
                      {onboarding.legalEntity.legalName}
                    </Text.Normal>
                  </Box>
                </List.Item>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </WizardStepLayout>
  )
}
